import { saveBlobOnUsersDevice } from 'services/fileOperations';
import { EXTENSIONS } from 'shared/constants/EXTENSIONS';
import { MIME_TYPES } from 'shared/constants/MIME_TYPES';
import type { ChartTable } from './chartTable';
import { toCsv } from './csv';

/** Elements marked with this attribute (e.g. header buttons) are skipped in snapshots. */
export const CHART_EXPORT_IGNORE_ATTRIBUTE = 'data-chart-export-ignore';

const SNAPSHOT_PIXEL_RATIO = 2;
const SNAPSHOT_PADDING = 24;
const FOOTER_FONT_SIZE = 13;
const FOOTER_HEIGHT = 32;
const PDF_MARGIN_MM = 10;
/** Long edge stays A4-sized so the file is still a normal page to open and print. */
const PDF_LONG_EDGE_MM = 297;
const UTF8_BOM = '﻿';

export type ChartSnapshotOptions = Readonly<{
  backgroundColor: string;
  /** Text drawn under the chart, e.g. generation date. */
  footer?: string;
  footerColor?: string;
  fontFamily?: string;
}>;

/**
 * Renders the chart card DOM (SVG + HTML legend/center total) to a canvas.
 * Text is rasterized, so any script renders correctly in PNG and PDF.
 */
async function renderChartSnapshot(
  node: HTMLElement,
  options: ChartSnapshotOptions
): Promise<HTMLCanvasElement> {
  const { toCanvas } = await import('html-to-image');
  const chartCanvas = await toCanvas(node, {
    pixelRatio: SNAPSHOT_PIXEL_RATIO,
    backgroundColor: options.backgroundColor,
    filter: (element) =>
      !(
        element instanceof HTMLElement &&
        element.hasAttribute(CHART_EXPORT_IGNORE_ATTRIBUTE)
      ),
  });

  const ratio = SNAPSHOT_PIXEL_RATIO;
  const padding = SNAPSHOT_PADDING * ratio;
  const footerHeight = options.footer ? FOOTER_HEIGHT * ratio : 0;
  const canvas = document.createElement('canvas');
  canvas.width = chartCanvas.width + padding * 2;
  canvas.height = chartCanvas.height + padding * 2 + footerHeight;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas 2D context is unavailable');
  }
  context.fillStyle = options.backgroundColor;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(chartCanvas, padding, padding);

  if (options.footer) {
    context.fillStyle = options.footerColor ?? '#000000';
    context.font = `${FOOTER_FONT_SIZE * ratio}px ${options.fontFamily ?? 'sans-serif'}`;
    context.textBaseline = 'middle';
    context.fillText(
      options.footer,
      padding,
      padding + chartCanvas.height + footerHeight / 2
    );
  }

  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Failed to encode chart snapshot'));
      }
    }, MIME_TYPES.IMAGE_PNG);
  });
}

export async function exportChartPng(
  node: HTMLElement,
  fileName: string,
  options: ChartSnapshotOptions
): Promise<void> {
  const canvas = await renderChartSnapshot(node, options);
  await saveBlobOnUsersDevice(await canvasToBlob(canvas), `${fileName}.${EXTENSIONS.PNG}`);
}

/**
 * Page size follows the chart. A fixed A4 page leaves a large empty band when
 * the snapshot is much wider or taller than A4.
 */
export function getChartPdfPageSize(
  imageWidth: number,
  imageHeight: number
): { width: number; height: number } {
  const safeWidth = Math.max(imageWidth, 1);
  const safeHeight = Math.max(imageHeight, 1);
  const contentLongEdge = PDF_LONG_EDGE_MM - PDF_MARGIN_MM * 2;
  const aspect = safeWidth / safeHeight;
  const contentWidth = aspect >= 1 ? contentLongEdge : contentLongEdge * aspect;
  const contentHeight = aspect >= 1 ? contentLongEdge / aspect : contentLongEdge;

  return {
    width: contentWidth + PDF_MARGIN_MM * 2,
    height: contentHeight + PDF_MARGIN_MM * 2,
  };
}

export async function exportChartPdf(
  node: HTMLElement,
  fileName: string,
  options: ChartSnapshotOptions
): Promise<void> {
  const [canvas, { jsPDF }] = await Promise.all([
    renderChartSnapshot(node, options),
    import('jspdf'),
  ]);
  const page = getChartPdfPageSize(canvas.width, canvas.height);
  const pdf = new jsPDF({
    orientation: page.width >= page.height ? 'landscape' : 'portrait',
    unit: 'mm',
    format: [page.width, page.height],
  });
  const contentWidth = pdf.internal.pageSize.getWidth() - PDF_MARGIN_MM * 2;
  const contentHeight = pdf.internal.pageSize.getHeight() - PDF_MARGIN_MM * 2;
  const scale = Math.min(
    contentWidth / canvas.width,
    contentHeight / canvas.height
  );
  const width = canvas.width * scale;
  const height = canvas.height * scale;

  pdf.addImage(
    canvas,
    EXTENSIONS.PNG.toUpperCase(),
    PDF_MARGIN_MM + (contentWidth - width) / 2,
    PDF_MARGIN_MM + (contentHeight - height) / 2,
    width,
    height,
    undefined,
    'FAST'
  );
  await saveBlobOnUsersDevice(pdf.output('blob'), `${fileName}.${EXTENSIONS.PDF}`);
}

export async function exportChartCsv(
  table: ChartTable,
  fileName: string
): Promise<void> {
  const blob = new Blob([UTF8_BOM, toCsv(table)], {
    type: `${MIME_TYPES.CSV};charset=utf-8`,
  });
  await saveBlobOnUsersDevice(blob, `${fileName}.${EXTENSIONS.CSV}`);
}

/** Excel limits sheet names to 31 chars and forbids some characters. */
function toSheetName(name: string): string {
  return name.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31).trim() || 'Chart';
}

export async function exportChartXlsx(
  table: ChartTable,
  sheetName: string,
  fileName: string
): Promise<void> {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(toSheetName(sheetName));

  worksheet.addRow([...table.headers]).font = { bold: true };
  table.rows.forEach((row) => worksheet.addRow([...row]));
  const allRows = [table.headers, ...table.rows];
  worksheet.columns.forEach((column, index) => {
    const lengths = allRows.map(
      (row) => String(row[index] ?? '').length
    );
    column.width = Math.min(Math.max(...lengths, 8) + 2, 60);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  await saveBlobOnUsersDevice(
    new Blob([buffer], { type: MIME_TYPES.EXCEL_XLSX }),
    `${fileName}.${EXTENSIONS.XLSX}`
  );
}

export const CHART_EXPORT_FORMATS = [
  EXTENSIONS.PNG,
  EXTENSIONS.PDF,
  EXTENSIONS.CSV,
  EXTENSIONS.XLSX,
] as const;

export type ChartExportFormat = (typeof CHART_EXPORT_FORMATS)[number];

/** `<title>-<YYYY-MM-DD>`, safe for all file systems. */
export function buildChartExportFileName(title: string, date: Date): string {
  const safeTitle =
    title
      .replace(/[\\/:*?"<>|]/g, '')
      .replace(/\s+/g, '_')
      .trim() || 'chart';
  const pad = (value: number): string => String(value).padStart(2, '0');
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return `${safeTitle}-${day}`;
}
